import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

import {
  Tooltip,
  Dialog,
  useTheme,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import SoftInput from "../../components/SoftInput";
import SoftButton from "../../components/SoftButton";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { startCase, capitalize, debounce } from "lodash";
import toast from "react-hot-toast";
import { UseIsAdmin, UseCompanyId } from "../../hooks/auth";
import { KPI_IMAGE_PARAMS, KPI_TYPES } from "../../constants/kpi";
import Geolocation from "../../components/Geolocation";
import { useKpiSubmit } from "../../hooks/kpi";
import { GET_KPI_DETAIL_VALUES } from "../../api/kpi";
import { usePastMonthSaveMutation } from "../../hooks/usePastMonthKpi";
import { UseSelectedWorkspace } from "../../hooks/company";
import { companyMonthStr } from "../../helpers/companyTime";
import { useOnlineStatus } from "../../hooks/useNetwork";
import { useSyncOfflineData } from "../../hooks/useSyncOfflineData";
import { resizeImage } from "../../helpers/images";
import { toErrorMessage } from "../../helpers/errorText";
import QuickCapture from "../../components/QuickCapture/QuickCapture";

const KpiModal = ({
  userId,
  kpi,
  index,
  month,
  userPoints,
  disabled = false,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [requiredInputs, setRequiredInputs] = useState([]);
  const [metadata, setMetadata] = useState({});
  // Detail-value suggestions per text input (e.g. Memberships' plan names) —
  // previously entered values become a pick-or-type dropdown so "Clean 365"
  // is selected, not retyped and misspelled.
  const [detailSuggestions, setDetailSuggestions] = useState({});
  // Hours Worked's KpiModel kind is "#" (Item) for display formatting (plain
  // number, no $/%), but it is NOT a bare item counter — it takes a real
  // decimal hour amount per entry, same as a monetary metric. Treating it as
  // isItem hid the amount field and force-submitted "1" on every add,
  // silently recording "+1 hour" no matter what the user typed.
  const isHoursWorkedKpi =
    kpi?.model?.type === "hours_worked" || kpi?.type === "hours_worked";
  const [isItem] = useState(kpi.kind === "#" && !isHoursWorkedKpi);
  const [snapshotMode, setSnapshotMode] = useState(false);
  // Bid Accuracy form (GroupKpi.parameters.bidAccuracyForm): each yes/no
  // question is worth 100 / question count and the job score is the sum of
  // the checked ("yes") answers — unchecked answers deduct their share from
  // the job's default 100. The computed score is sent with the entry so the
  // server can average it into the Bid Accuracy metric.
  const isBidAccuracyForm = Boolean(kpi?.parameters?.bidAccuracyForm);
  const bidQuestionKeys = isBidAccuracyForm
    ? (kpi.requiredInputs ?? [])
        .filter((input) => input.type === "checkbox")
        .map((input) => input.value)
    : [];
  const bidScore = bidQuestionKeys.length
    ? Math.round(
        bidQuestionKeys.filter((key) => metadata[key] === true).length *
          (100 / bidQuestionKeys.length) *
          10,
      ) / 10
    : null;
  const isOnlyQuickCapture = kpi.requiredInputs?.length > 0 && kpi.requiredInputs.every((inp) => inp.type === "quick-capture");
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const inputRef = useRef();
  const locationRef = useRef();
  const [hasLocation, setHasLocation] = useState(false);
  const theme = useTheme();
  const isAdmin = UseIsAdmin();
  const isAdminOnly = kpi?.isAdminAccess && !isAdmin;
  const { submitKpi, isSubmitting: isCurrentSubmitting } = useKpiSubmit();
  const pastMonthMutation = usePastMonthSaveMutation();
  const isSubmitting = isCurrentSubmitting || pastMonthMutation.isLoading;
  const isPastMonth = month != null && moment(month).format("YYYY-MM") < companyMonthStr();
  const companyId = UseCompanyId();
  const currentWorkspace = UseSelectedWorkspace();
  const isOnline = useOnlineStatus();
  const { syncOfflineData } = useSyncOfflineData();

  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const inputFieldStyle = {
    borderRadius: "6px",
    border: "1px solid #E4E7EC",
    backgroundColor: "#F6F7F9 !important",
    color: "#1d2939 !important",
    "& .MuiInputBase-input::placeholder": {
      color: "#98A2B3 !important",
      opacity: "1",
    },
    "&.Mui-focused": {
      borderColor: primaryColor,
      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
    },
  };

  const setInput = (inputParam, value) => {
    if (inputParam.value && value) {
      const data = { ...metadata };
      data[inputParam.value] = value;
      setMetadata(data);
    }
  };

  const handleClose = () => {
    setProcessing(false);
    setOpen(false);
    setError("");
    props.onClose?.();
  };

  const getTooltipText = () => {
    if (isAdminOnly) {
      return "Players do not have input access for this KPI";
    } else {
      return kpi.isManual
        ? ""
        : "Manual input is allowed, but not recommended for integrated KPIs";
    }
  };

  const handleSubmit = debounce(async () => {
    const adding = toast.loading("Adding Kpi...");
    setProcessing(true);
    const formData = new FormData();
    for (let i = 0; i < requiredInputs.length; i += 1) {
      const item = requiredInputs[i];
      const key = kpi.requiredInputs[i].value;
      if (item.type === "quick-capture") continue;
      if (item.type !== "image" && item.type !== "picture") {
        // Optional inputs (e.g. Note) may be left blank — don't send the
        // literal string "undefined" into the entry's metadata.
        if (metadata[key] == null || metadata[key] === "") continue;
        formData.append(key, metadata[key]);
      } else {
        // eslint-disable-next-line no-await-in-loop
        const resizedImage = await resizeImage(metadata[key]);
        formData.append("image", resizedImage ?? metadata[key]);
        formData.append("imageKey", key);
      }
    }

    let amountValue = amount;

    if (isItem) {
      // "#" Item types always send amount=1
      amountValue = 1;
    } else if (kpi.kind === "%" && kpi?.metricType === KPI_TYPES[1]) {
      // Divide by 100 if "%" type anchor metric
      amountValue = parseFloat(amountValue) / 100;
    }

    if (isBidAccuracyForm && bidQuestionKeys.length) {
      // Store the computed job score (0-100) on the entry's metadata; the
      // server averages these into the workspace's Bid Accuracy metric.
      formData.append("score", bidScore);
    }

    formData.append("amount", amountValue);
    try {
      if (isPastMonth) {
        await pastMonthMutation.mutateAsync({
          kpiId: kpi._id,
          userId,
          monthKey: moment(month).format("YYYY-MM"),
          data: formData,
          companyId,
          workspaceId: currentWorkspace?._id,
        });
      } else {
        await submitKpi(kpi._id, userId, month, formData, companyId);
      }
      toast.success("Score Added", { id: adding });
      // Pass the amount that was just added so the cell can show a
      // "+$amount" floater celebration. Falls back gracefully if the
      // caller doesn't care about the payload.
      props.onSave?.({ amount: amountValue, kind: kpi?.kind });
      // Reset the values of formData by deleting all its keys
      Array.from(formData.keys()).forEach((key) => {
        formData.delete(key);
      });
      // setTimeout(() => {
      //   syncOfflineData(isOnline);
      // }, 100);
      handleClose();
      setAmount("");
      setMetadata({});
      setHasLocation(false);
    } catch (err) {
      toast.error(toErrorMessage(err, "Could not save"), { id: adding });
    }
  }, 500);

  useEffect(() => {
    let hasImage = false;
    if (kpi.requiredInputs && kpi.requiredInputs.length) {
      const inputs = [];
      let inputCount = 0;
      kpi.requiredInputs.forEach((input) => {
        const { value, type, optional } = input;
        const isImage = KPI_IMAGE_PARAMS.includes(type);
        const isLocation = type === "location";
        let label = (value ?? type)
          .replaceAll("_", " ")
          .replaceAll(/\w+/g, capitalize);
        if (optional) label += " (optional)";
        inputs.push({ label, value, type });
        hasImage = hasImage || isImage;
        inputCount += !isLocation ? 1 : 0; // Geolocation is hidden as does not count as an "input"
        setHasLocation((prevValue) => prevValue || isLocation);
      });
      // setSnapshotMode(hasImage && inputCount === 1);
      setRequiredInputs(inputs);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [kpi.requiredInputs]);

  // Load previously used values for the metric's text inputs when the modal
  // opens. The dropdown only activates when values genuinely repeat (a plan
  // name does; a one-off customer note doesn't), so free-text fields keep
  // their plain input.
  useEffect(() => {
    if (!open) return undefined;
    const textInputs = (kpi.requiredInputs || []).filter((inp) => inp.type === "text" && inp.value);
    if (!textInputs.length) return undefined;
    let alive = true;
    textInputs.forEach((inp) => {
      GET_KPI_DETAIL_VALUES(kpi._id, inp.value)
        .then((res) => {
          const payload = res?.data ?? res;
          const values = Array.isArray(payload?.values) ? payload.values : [];
          if (!alive || !values.some((v) => Number(v.count) >= 2)) return;
          setDetailSuggestions((prev) => ({ ...prev, [inp.value]: values.map((v) => v.value) }));
        })
        .catch(() => {});
    });
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, kpi._id]);

  const validateInputs = (e) => {
    e?.preventDefault();

    if (requiredInputs.length) {
      for (let k = 0; k < kpi.requiredInputs.length; k++) {
        const param = kpi.requiredInputs[k].value;
        const { type } = kpi.requiredInputs[k];
        if (type === 'checkbox' || type === 'notification_settings' || type === 'quick-capture') continue;
        // Inputs flagged optional in the KPI config (e.g. Up Sell "Note") may
        // be left blank without blocking the submit.
        if (kpi.requiredInputs[k].optional && !metadata[param]) continue;
        if (type === 'link' && (!metadata[param]?.includes('.') || 
            (metadata[param]?.startsWith('http') && !metadata[param]?.includes('://')) ||
            (metadata[param]?.startsWith('www') && !metadata[param]?.includes('.')))) {
          setError(`Please enter a valid URL`);
          return;
        }
        if (!metadata[param]) {
          setError(`Please complete KPI inputs. Missing: ${startCase(param)}`);
          return;
        }
      }
    }
    if (!isItem && !amount) {
      setError("Please input KPI amount.");
      return;
    }
    handleSubmit();
  };

  useEffect(() => {
    // Go straight to submission for one-image submissions
    if (snapshotMode && Object.keys(metadata).length) {
      validateInputs();
    }
  }, [snapshotMode, metadata, validateInputs]);

  return (
    <>
      <Tooltip title={getTooltipText()}>
        <SoftBox
          sx={{ cursor: "pointer" }}
          onClick={() => {
            // Disable showing the KPI modal pop-up
            if (disabled) return;
            if (isAdminOnly) return;

            setOpen(true);
            // Activate geolocation; this can only be done on user interaction
            if (hasLocation) {
              locationRef.current?.activate();
            }
            props.onClick?.();
            if (snapshotMode) {
              // Open camera on snapshot mode, but with minor delay to make sure that dialog components have already loaded
              setTimeout(() => inputRef?.current?.click(), 250);
            }
          }}
        >
          {props.children}
        </SoftBox>
      </Tooltip>
      <Dialog
        aria-labelledby="unstyled-modal-title"
        aria-describedby="unstyled-modal-description"
        open={open}
        onClose={handleClose}
        sx={{ margin: "auto", width: { xs: "100vw", sm: "60vw" } }}
        PaperProps={{
          sx: {
            width: "100%",
            borderRadius: "24px",
            border: `3px solid ${primaryColor}`,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
            boxShadow:
              "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
            backdropFilter: "blur(18px) saturate(160%)",
          },
        }}
      >
        <SoftBox
          sx={{
            paddingX: 3,
            paddingTop: 3,
            paddingBottom: 0,
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SoftBox>
              <SoftTypography variant="h4" sx={{ color: "#0F172A" }}>
                {kpi.title}
              </SoftTypography>
              <SoftTypography variant="body2" sx={{ color: "#475467" }}>
                Enter {kpi.title} score
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="outlined"
              onClick={handleClose}
              sx={{
                "& svg": { fontSize: "1.25rem !important" },
                borderRadius: "50%",
                minWidth: "max-content",
                minHeight: "max-content",
                padding: "0.25rem",
                color: "#475467",
              }}
            >
              <CloseRoundedIcon />
            </SoftButton>
          </SoftBox>
        </SoftBox>
        <Divider
          sx={{
            backgroundImage: `linear-gradient(to right, rgba(52, 71, 103, 0), ${primaryColor}, rgba(52, 71, 103, 0)) !important`,
            "&:not([size])": {
              height: "2px",
            },
          }}
        />
        <SoftBox
          sx={{
            paddingX: 3,
            paddingY: 0,
          }}
        >
          <SoftTypography variant="caption" sx={{ color: "#475467" }}>
            Current score: {kpi?.kind === "%" ? formatKpiPercent(kpi, userPoints) : formatNumber(userPoints, kpi?.kind)}
          </SoftTypography>
        </SoftBox>

        <form>
          <SoftBox
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingX: 3,
              paddingY: 3,
              gap: 2,
            }}
          >
            {!isItem && !isOnlyQuickCapture && (
              <SoftBox
                display="flex"
                flexDirection="column"
                gap="0.75rem"
                justifyContent="center"
              >
                <SoftInput
                  id="outlined-basic"
                  placeholder="Add amount"
                  type="number"
                  size="large"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  sx={inputFieldStyle}
                />
              </SoftBox>
            )}
            {requiredInputs &&
              requiredInputs.map((input, inputIdx) => {
                const { type, label, value: inputVal } = input;

                // Hide notification settings input
                if (type === "notification_settings") {
                  return (
                    <div key={`${label}`} style={{display: 'none'}}>
                      <SoftInput
                        placeholder={label}
                        id="outlined-basic"
                        type="text"
                        onChange={(e) =>
                          setInput(kpi.requiredInputs[inputIdx], e.target.value)
                        }
                        sx={inputFieldStyle}
                      />
                    </div>
                  );
                }

                // Image submission input
                if (KPI_IMAGE_PARAMS.includes(type)) {
                  return (
                    <>
                      <SoftBox className="flex flex-col items-center w-full gap-2">
                        <SoftButton
                          variant="outlined"
                          fullWidth
                          onClick={() => inputRef?.current.click()}
                        >
                          Upload {label}
                        </SoftButton>
                        {metadata?.[kpi.requiredInputs[inputIdx].value] && (
                          <SoftBox
                            borderRadius="lg"
                            className="flex w-3/5 justify-center overflow-hidden"
                          >
                            <img
                              src={URL.createObjectURL(
                                metadata[kpi.requiredInputs[inputIdx].value],
                              )}
                              alt={label}
                              style={{
                                objectFit: "contain",
                                maxWidth: "100%",
                                maxHeight: "320px",
                                width: "auto",
                                height: "auto",
                              }}
                            />
                          </SoftBox>
                        )}
                      </SoftBox>
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        ref={inputRef}
                        hidden
                        onChange={(e) => {
                          if (e.target.files.length === 0) {
                            return;
                          }
                          setInput(
                            kpi.requiredInputs[inputIdx],
                            e.target.files[0],
                          );
                          inputRef.current.value = null;
                        }}
                      />
                    </>
                  );
                } else if (type === "checkbox") {
                  return (
                    <FormControlLabel
                      key={`${label}`}
                      control={
                        <Checkbox
                          sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
                          onChange={(e) =>
                            setInput(
                              kpi.requiredInputs[inputIdx],
                              e.target.checked,
                            )
                          }
                        />
                      }
                      // Bid Accuracy questions are yes/no answers, not
                      // optional extras — keep their labels clean.
                      label={isBidAccuracyForm ? label : `${label} (optional)`}
                      sx={{
                        margin: 0,
                        "& .MuiFormControlLabel-label": {
                          color: "#0F172A",
                          fontWeight: 400,
                          fontSize: "1rem",
                        },
                      }}
                    />
                  );
                } else if (type === "quick-capture") {
                  return (
                    <SoftBox
                      key={`${label}`}
                      onClick={() => { setOpen(false); setQuickCaptureOpen(true); }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        padding: "1.5rem",
                        cursor: "pointer",
                        background: "rgba(124, 58, 237, 0.15)",
                        border: "1px solid rgba(124, 58, 237, 0.30)",
                        "&:hover": { background: "rgba(124, 58, 237, 0.25)" },
                        transition: "all 0.2s",
                      }}
                    >
                      <SoftTypography sx={{ fontSize: "2rem", mb: 1 }}>📷</SoftTypography>
                      <SoftTypography variant="body2" sx={{ color: "#0F172A", fontWeight: 700, textAlign: "center" }}>
                        Open Live Camera
                      </SoftTypography>
                      <SoftTypography variant="caption" sx={{ color: "#475467", textAlign: "center", mt: 0.5 }}>
                        Camera + GPS address
                      </SoftTypography>
                    </SoftBox>
                  );
                } else if (type === "location") {
                  return null;
                } else if (type === "dropdown") {
                  // Admin-defined choices from the metric's form config —
                  // pick one, no free typing.
                  const dropdownOptions = kpi.requiredInputs[inputIdx]?.options || [];
                  return (
                    <div key={`${label}`}>
                      <Autocomplete
                        options={dropdownOptions}
                        value={metadata[inputVal] ?? null}
                        onChange={(e, newValue) => {
                          if (newValue == null) {
                            // Clearing the pick must clear the metadata too, or
                            // the old choice would silently ride along.
                            setMetadata((prev) => {
                              const next = { ...prev };
                              delete next[inputVal];
                              return next;
                            });
                            return;
                          }
                          setInput(kpi.requiredInputs[inputIdx], newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={label}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                border: "1px solid #E4E7EC",
                                backgroundColor: "#F6F7F9",
                                color: "#1d2939",
                                fontSize: "0.875rem",
                                padding: "6px 10px",
                                "& fieldset": { border: "none" },
                                "&.Mui-focused": {
                                  borderColor: primaryColor,
                                  boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                                },
                              },
                              "& .MuiOutlinedInput-input::placeholder": {
                                color: "#98A2B3",
                                opacity: 1,
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                  );
                }
                else {
                  const isMultiline = ["description", "note"].includes(inputVal);
                  const suggestions = type === "text" ? detailSuggestions[inputVal] || [] : [];
                  // Repeating detail values (Memberships plan names) become a
                  // pick-or-type combobox — existing names are offered, but a
                  // brand-new one can still be typed in.
                  if (suggestions.length) {
                    return (
                      <div key={`${label}`}>
                        <Autocomplete
                          freeSolo
                          options={suggestions}
                          onInputChange={(e, newValue) =>
                            setInput(kpi.requiredInputs[inputIdx], newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={label}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "6px",
                                  border: "1px solid #E4E7EC",
                                  backgroundColor: "#F6F7F9",
                                  color: "#1d2939",
                                  fontSize: "0.875rem",
                                  padding: "6px 10px",
                                  "& fieldset": { border: "none" },
                                  "&.Mui-focused": {
                                    borderColor: primaryColor,
                                    boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                                  },
                                },
                                "& .MuiOutlinedInput-input::placeholder": {
                                  color: "#98A2B3",
                                  opacity: 1,
                                },
                              }}
                            />
                          )}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={`${label}`}>
                      <SoftInput
                        placeholder={label}
                        id="outlined-basic"
                        type={type}
                        defaultValue={
                          type === "date" ? moment().format("yyyy-MM-DD") : ""
                        }
                        onChange={(e) =>
                          setInput(kpi.requiredInputs[inputIdx], e.target.value)
                        }
                        multiline={isMultiline}
                        maxRows={isMultiline ? 8 : 1}
                        sx={inputFieldStyle}
                      />
                    </div>
                  );
                }
              })}
            {isBidAccuracyForm && bidQuestionKeys.length > 0 && (
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #E4E7EC",
                  backgroundColor: "#F6F7F9",
                  padding: "10px 16px",
                }}
              >
                <SoftTypography
                  sx={{ color: "#475467", fontSize: "0.9rem", fontWeight: 500 }}
                >
                  Job Score
                </SoftTypography>
                <SoftTypography
                  sx={{ color: "#0F172A", fontSize: "1.1rem", fontWeight: 700 }}
                >
                  {bidScore} / 100
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>

          {!isOnlyQuickCapture && (
            <div style={{ padding: 4, marginBottom: 20 }}>
              {error && (
                <p className="text-center text-red-500 mb-0">
                  {toErrorMessage(error)}
                </p>
              )}
              <SoftButton
                variant="gradient"
                color="primary"
                onClick={validateInputs}
                disabled={processing || isSubmitting}
                sx={{
                  textTransform: "none",
                  width: "max-content",
                  padding: "14px 58px",
                  fontSize: "1.1rem",
                  display: "block",
                  margin: "0 auto",
                  marginTop: "30px",
                  borderRadius: "8px",
                  color: "#FFF",
                  boxShadow:
                    "0px 2px 4px -1px rgba(0, 0, 0, 0.07), 0px 4px 6px -1px rgba(0, 0, 0, 0.12)",
                }}
              >
                Add {kpi.title ?? "KPI"}
              </SoftButton>
            </div>
          )}
          {/* <button
            type="submit"
            className="d-block mx-auto my-3 px-5"
            sx={{ border: "1px solid #FF0080" }}
          >
            Add
          </button> */}
        </form>
      </Dialog>
      <Geolocation
        ref={locationRef}
        onLocated={({ lat, lng, error: locationErr }) => {
          if (locationErr) {
            setError(toErrorMessage(locationErr, "Location unavailable"));
          } else {
            setInput("location", `${lat},${lng}`);
          }
        }}
        hidden
      />
      <QuickCapture
        kpi={kpi}
        userId={userId}
        companyId={companyId}
        open={quickCaptureOpen}
        onClose={() => setQuickCaptureOpen(false)}
      />
    </>
  );
};

export default KpiModal;